




































import { computed, defineComponent, PropType, ref, toRef, watch, onUnmounted, onMounted } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { TagInfo, TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { ContentFeedSearchResult } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useIntersectionObserver from "@/hooks/useIntersectionObserver";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { TabField } from "@/types";
import BaseIcon from "@/components/BaseIcon";
import BasePage from "@/components/BasePage";
import ContentFeedView from "@/components/ContentFeedView";
import CreatorsSlider from "@/components/CreatorsSlider";
import EmptyContent from "@/components/EmptyContent";
import PageTitle from "@/components/PageTitle";
import TabsHeader from "@/components/TabsHeader";
import UnauthorizedAgeConfirm from "@/components/UnauthorizedAgeConfirm";
import DiscoverFilters from "../DiscoverFilters";
import DiscoverLivePreviews from "../DiscoverLivePreviews";

export default defineComponent({
  name: "DiscoverView",
  components: {
    UnauthorizedAgeConfirm,
    DiscoverLivePreviews,
    DiscoverFilters,
    BaseIcon,
    BasePage,
    ContentFeedView,
    CreatorsSlider,
    EmptyContent,
    PageTitle,
    TabsHeader,
  },
  props: {
    onFeed: { type: Boolean, required: true },
    loading: { type: Boolean, required: true },
    enableScroll: { type: Boolean, required: true },
    selectedTags: { type: Array as () => TagInfo[], required: true },
    selectedSort: { type: String, default: "random" },
    followedUsersId: { type: Object as () => Record<string, boolean>, required: true },
    contentFeedSearchResult: { type: Object as () => ContentFeedSearchResult | null },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    tagsType: { type: String as () => TagInfoType, required: true },
    usersInLive: { type: Array as PropType<UserPublicData[]>, default: () => [] },
  },
  setup(props, { root, emit }) {
    const { authUser } = useAuthUser();
    const feedId = ref<string | null>(null);
    const livePreviewsRef = ref<HTMLElement | null>(null);
    const showCreatorsSlider = computed(() => config.featureDiscoveryCreators);
    const emptyDiscover = computed(() => {
      return !props.loading && props.contentFeedSearchResult?.result.length === 0;
    });

    const { setupIntersectionObserver, cleanupIntersectionObserver } = useIntersectionObserver({
      elementRef: livePreviewsRef,
      callback: () => {
        emit("updatePreviews");
      },
    });

    const tabs = computed(() => {
      const list: TabField[] = [
        {
          name: "home",
          text: $t("Home"),
          to: { name: "home" },
        },
        {
          name: "discover",
          text: $t("Discover"),
          to: { name: "discover" },
        },
      ];
      return (authUser.value && list) || list.filter(i => i.name !== "home");
    });

    const openFeed = (openFeedId: string) => {
      feedId.value = openFeedId;
      root.$router.push({
        name: "discoverFeed",
        query: root.$route.query,
      });
    };

    watch(
      toRef(props, "onFeed"),
      newValue => {
        if (!newValue && feedId.value) {
          feedId.value = null;
        }
      },
      { immediate: true }
    );
    watch(
      toRef(props, "contentFeedSearchResult"),
      (newValue, oldValue) => {
        if (!oldValue && newValue && feedId.value === null && props.onFeed) {
          feedId.value = newValue.result.find(() => true)?.id ?? null;
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      setupIntersectionObserver();
    });

    onUnmounted(() => {
      cleanupIntersectionObserver();
    });

    return {
      feedId,
      livePreviewsRef,
      showCreatorsSlider,
      tabs,
      emptyDiscover,
      authUser,
      openFeed,
    };
  },
});
